import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../services/auth.service';

/** One row of `company_job_schedule`, as the settings page sees it. */
interface JobScheduleDto {
  id: number;
  jobKey: string;
  displayName: string;
  description: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  /** "HH:mm", company local. */
  runAt: string;
  isEnabled: boolean;
  lastRunLocalDate: string | null;
  lastRunAt: string | null;
  lastRunStatus: 'Success' | 'Failed' | null;
  lastRunNote: string | null;
  nextRunLocalDate: string | null;
}

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * When this company's recurring jobs run.
 *
 * These times were constants in the backend, and three of the six jobs were
 * never invoked at all — so the page exists to make the schedule something the
 * business owns rather than something only a deploy can change.
 */
@Component({
  selector: 'app-job-schedules',
  templateUrl: './job-schedules.component.html',
  styleUrls: ['./job-schedules.component.css'],
})
export class JobSchedulesComponent implements OnInit {
  rows: JobScheduleDto[] = [];
  loading = false;
  loadError: string | null = null;
  savingId: number | null = null;
  runningId: number | null = null;

  /** Company time zone, echoed so "04:00" is unambiguous on this page. */
  timeZoneId: string | null = null;
  timeZoneConfigured = false;

  readonly dayLabels = DAY_LABELS;
  readonly daysOfMonth = Array.from({ length: 28 }, (_, i) => i + 1);

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public auth: AuthService,
  ) {}

  get canManage(): boolean {
    return this.auth.hasPermission('JobSchedule.Manage');
  }

  ngOnInit(): void {
    this.load();
    this.http.get<{ timeZoneId: string | null; isConfigured: boolean }>('/api/company/time-zone')
      .subscribe({
        next: tz => { this.timeZoneId = tz.timeZoneId; this.timeZoneConfigured = tz.isConfigured; },
        error: () => { /* non-fatal — the banner just won't name the zone */ },
      });
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.http.get<JobScheduleDto[]>('/api/company/job-schedules').subscribe({
      next: rows => { this.rows = rows; this.loading = false; },
      error: () => {
        this.loadError = 'Failed to load job schedules.';
        this.loading = false;
      },
    });
  }

  /** Clear the fields the new frequency does not use, and supply a sane default. */
  onFrequencyChange(row: JobScheduleDto): void {
    if (row.frequency === 'weekly') {
      row.dayOfMonth = null;
      row.dayOfWeek ??= 1;
    } else if (row.frequency === 'monthly') {
      row.dayOfWeek = null;
      row.dayOfMonth ??= 1;
    } else {
      row.dayOfWeek = null;
      row.dayOfMonth = null;
    }
  }

  save(row: JobScheduleDto): void {
    if (!this.canManage) return;
    this.savingId = row.id;
    this.http.put(`/api/company/job-schedules/${row.id}`, {
      frequency: row.frequency,
      dayOfWeek: row.dayOfWeek,
      dayOfMonth: row.dayOfMonth,
      runAt: row.runAt,
      isEnabled: row.isEnabled,
    }).subscribe({
      next: () => {
        this.savingId = null;
        this.snackBar.open(`${row.displayName} schedule saved`, undefined, { duration: 2500 });
        this.load();   // re-read so "next run" reflects what was just saved
      },
      error: e => {
        this.savingId = null;
        this.snackBar.open(e?.error?.error ?? 'Failed to save the schedule.', 'OK', { duration: 5000 });
      },
    });
  }

  runNow(row: JobScheduleDto): void {
    if (!this.canManage) return;
    // Jobs can modify operational state or trigger external effects, so a
    // manual run is confirmed rather than one click away.
    if (!confirm(`Run "${row.displayName}" now?\n\n${row.description}`)) return;

    this.runningId = row.id;
    this.http.post<{ status: string; note: string }>(
      `/api/company/job-schedules/${row.id}/run`, {}).subscribe({
      next: r => {
        this.runningId = null;
        this.snackBar.open(`${row.displayName}: ${r.note}`, 'OK', { duration: 6000 });
        this.load();
      },
      error: e => {
        this.runningId = null;
        this.snackBar.open(
          `${row.displayName} failed: ${e?.error?.error ?? 'unknown error'}`, 'OK', { duration: 8000 });
        this.load();
      },
    });
  }

  statusClass(status: string | null): string {
    if (status === 'Success') return 'chip chip-success';
    if (status === 'Failed') return 'chip chip-failed';
    return 'chip chip-none';
  }
}
